'use client';

import { EditBannerModal } from '@/components/tournament/details-page/edit-modals/EditBannerModal';
import { EditCategoriesModal } from '@/components/tournament/details-page/edit-modals/EditCategoriesModal';
import { EditDatesAndScheduleModal } from '@/components/tournament/details-page/edit-modals/EditDatesAndScheduleModal';
import { EditDescriptionModal } from '@/components/tournament/details-page/edit-modals/EditDescriptionModal';
import { EditGenderModal } from '@/components/tournament/details-page/edit-modals/EditGenderModal';

import {
  cancelTournament,
  getTournamentById,
  updateTournament,
} from '@/api/tournament/tournamentApi';
import { useTournamentStore } from '@/store/useTournamentStore';
import { EditNameModal } from '@/components/tournament/details-page/edit-modals/EditNameModal';
import { EditSignupDeadlineModal } from '@/components/tournament/details-page/edit-modals/EditSignupDeadlineModal';
import { EditPlayersPerTeamModal } from '@/components/tournament/details-page/edit-modals/EditPlayersPerTeamModal';
import { EditPriceModal } from '@/components/tournament/details-page/edit-modals/EditPriceModal';
import { EditPrizesModal } from '@/components/tournament/details-page/edit-modals/EditPrizesModal';
import { EditTeamsAllowedModal } from '@/components/tournament/details-page/edit-modals/EditTeamsAllowedModal';
import { EditAgeRestrictionModal } from '@/components/tournament/details-page/edit-modals/EditAgeRestrictionModal';
import { EditOverflowGroupClassificationModal } from '@/components/tournament/details-page/edit-modals/EditOverflowGroupClassificationModal';
import { EditAmericanGroupFormatModal } from '@/components/tournament/details-page/edit-modals/EditAmericanGroupFormatModal';
import { EditMinSchedulesRequiredModal } from '@/components/tournament/details-page/edit-modals/EditMinSchedulesRequiredModal';
import { EditCountsForCategoryRankingModal } from '@/components/tournament/details-page/edit-modals/EditCountsForCategoryRankingModal';
import { TournamentDetailsBanner } from '@/components/tournament/details-page/TournamenDetailstBanner';
import { TournamentDetailsDeleteDialog } from '@/components/tournament/details-page/TournamentDetailsDeleteDialog';
import { TournamentDetailsHeader } from '@/components/tournament/details-page/TournamentDetailsHeader';
import { TournamentDetailsInfoCard } from '@/components/tournament/details-page/TournamentDetailsInfoCard';
import { TournamentDetailsParticipantsCard } from '@/components/tournament/details-page/TournamentDetailsParticipantsCard';
import { finalDayRuleOf } from '@/components/tournament/details-page/participants/final-day-rule';
import { TournamentDetailsScheduleCard } from '@/components/tournament/details-page/TournamentDetailsScheduleCard';
import { TournamentPausedAlert } from '@/components/tournament/details-page/TournamentPausedAlert';
import { FixtureSection } from '@/components/tournament/fixture/FixtureSection';
import { RoundsCard } from '@/components/tournament/rounds/RoundsCard';
import { toast } from '@/components/ui/use-toast';
import type { Tournament, TournamentFormData } from '@/types';
import {
  AmericanGroupFormat,
  CATEGORY_RANKING_TARGET,
  OverflowGroupClassification,
  TournamentFormat,
} from '@/types/tournament.model';
import { resolveAmericanGroupFormat } from '@/utils/tournament-format';
import { handleGlobalError } from '@/utils/error-handler';
import {
  isTournamentCancelled,
  isTournamentInProgress,
} from '@/utils/tournament-status';
import { knockoutStartOf, mergeScheduleBlocks } from '@/utils/tournament-schedule';
import { hasPaymentsWithoutGateway } from '@/utils/tournament-cancellation';
import { useEloCategories } from '@/hooks/useEloCategories';
import { Loader2 } from 'lucide-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';

const TournamentDetailsPage = () => {
  const { id } = useParams<{ id: string }>();
  const refreshTournaments = useTournamentStore((s) => s.refreshTournaments);
  const [tournament, setTournament] = useState<Tournament | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [refundNoticeTournament, setRefundNoticeTournament] =
    useState<Tournament | null>(null);
  const [isCheckingPayments, setIsCheckingPayments] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);

  // State for edit modals - Always editable
  const [showEditNameModal, setShowEditNameModal] = useState(false);
  const [showEditBannerModal, setShowEditBannerModal] = useState(false);
  const [showEditPrizesModal, setShowEditPrizesModal] = useState(false);
  const [showEditDescriptionModal, setShowEditDescriptionModal] =
    useState(false);

  // State for edit modals - Only editable when !hasRegistrations
  const [showEditGenderModal, setShowEditGenderModal] = useState(false);
  const [showEditDatesAndScheduleModal, setShowEditDatesAndScheduleModal] =
    useState(false);
  const [showEditSignupDeadlineModal, setShowEditSignupDeadlineModal] =
    useState(false);
  const [showEditPriceModal, setShowEditPriceModal] = useState(false);
  const [showEditTeamsAllowedModal, setShowEditTeamsAllowedModal] =
    useState(false);
  const [showEditPlayersPerTeamModal, setShowEditPlayersPerTeamModal] =
    useState(false);
  const [showEditCategoriesModal, setShowEditCategoriesModal] = useState(false);
  const [showEditAgeRestrictionModal, setShowEditAgeRestrictionModal] =
    useState(false);
  const [
    showEditOverflowGroupClassificationModal,
    setShowEditOverflowGroupClassificationModal,
  ] = useState(false);
  const [
    showEditAmericanGroupFormatModal,
    setShowEditAmericanGroupFormatModal,
  ] = useState(false);
  const [
    showEditMinSchedulesRequiredModal,
    setShowEditMinSchedulesRequiredModal,
  ] = useState(false);
  const [
    showEditCountsForCategoryRankingModal,
    setShowEditCountsForCategoryRankingModal,
  ] = useState(false);

  const hasRegistrations = tournament ? tournament.teams?.length > 0 : false;
  const currentTeamsCount = tournament?.teams?.length ?? 0;

  const eloCategories = useEloCategories(tournament?.sport?.uid);
  const rankingRangeCategories = useMemo(() => {
    const min = tournament?.minCategory?.minimumElo;
    const max = tournament?.maxCategory?.minimumElo;
    if (min === undefined || max === undefined) return [];
    const low = Math.min(min, max);
    const high = Math.max(min, max);
    return eloCategories.filter(
      (category) => category.minimumElo >= low && category.minimumElo <= high,
    );
  }, [eloCategories, tournament?.minCategory, tournament?.maxCategory]);

  // The manager endpoint returns the schedule blocks unmerged (the schedule card below needs them
  // per club), and they arrive unordered. Anything that just wants "the days this tournament is
  // played on" has to merge them — reading schedule[0] shows an arbitrary block.
  const tournamentDays = useMemo(
    () => mergeScheduleBlocks(tournament?.schedule),
    [tournament?.schedule],
  );

  // Every guard below reads the tournament's own hasFixture rather than the /fixture
  // payload: that request is a 500 when no fixture exists and is swallowed on any other
  // failure too, so a transient error would read as "this tournament has no fixture" and
  // hand back the pencils, the delete buttons and "Registrar equipo". The flag is computed
  // backend-side and the fixture section refetches the tournament whenever it builds one.
  const hasFixture = !!tournament?.hasFixture;

  // While the tournament is being played, the manager works out of the fixture: the
  // config summary, the registrations and the schedule fold into their headers so
  // groups and knockouts get the screen. Without a fixture to look at there is nothing
  // to make room for, and folding them would just hide everything.
  const collapseSummaries =
    !!tournament && isTournamentInProgress(tournament) && hasFixture;

  const isLiveTournament = !!tournament && !isTournamentCancelled(tournament);

  // Registrations are not what makes the schedule unsafe to touch — an already built
  // fixture is. The backend agrees: it only refuses the change once the fixture is ACTIVE.
  const canEditSchedule = isLiveTournament && !hasFixture;

  // Same rule as the backend guard (`TournamentValidator.validateSignupDeadlineEditable`): the
  // deadline moves freely, registrations and all, until a fixture exists.
  const canEditSignupDeadline = isLiveTournament && !hasFixture;

  const loadTournament = useCallback(async (tournamentId: string) => {
    setTournament(await getTournamentById(tournamentId));
  }, []);

  useEffect(() => {
    // Reset edit mode when tournament changes
    setIsEditMode(false);

    const fetchTournament = async () => {
      if (!id) return;
      try {
        setLoading(true);
        await loadTournament(id);
      } catch (err) {
        setError('Error al cargar el torneo');
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    fetchTournament();
  }, [id, loadTournament]);

  const refreshTournamentData = async () => {
    if (!id) return;
    try {
      await loadTournament(id);
    } catch (err) {
      console.error('Error refreshing tournament data:', err);
    }
  };

  const handleUpdateTournament = async (
    updatedFields: Partial<TournamentFormData>,
  ) => {
    if (!id || !tournament) return;

    if (
      typeof updatedFields.maxTeamsAllowed === 'number' &&
      currentTeamsCount > 0 &&
      updatedFields.maxTeamsAllowed < currentTeamsCount
    ) {
      toast({
        variant: 'destructive',
        title: 'Límite de equipos inválido',
        description:
          'El máximo de equipos no puede ser menor a la cantidad de equipos ya inscriptos.',
      });
      return false;
    }
    setIsUpdating(true);
    try {
      const updatedTournament = await updateTournament(id, updatedFields);
      setTournament(updatedTournament);
      toast({
        title: 'Torneo actualizado',
        description: 'Los cambios se han guardado exitosamente.',
      });
      // Refresh the tournament list so it's reflected in My Tournaments
      refreshTournaments();
      return true;
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (err: any) {
      console.error('Error updating tournament:', err);
      handleGlobalError(
        err,
        'Error',
        'Ocurrió un error al actualizar el torneo.',
      );

      return false;
    } finally {
      setIsUpdating(false);
    }
  };

  // The refund notice depends on how many teams are complete, and a pair can finish paying while
  // the page is open: read the tournament again right before the organizer confirms.
  const openCancelDialog = async () => {
    if (!id) return;
    setRefundNoticeTournament(null);
    setIsCheckingPayments(true);
    setShowDeleteDialog(true);
    try {
      const freshTournament = await getTournamentById(id);
      setTournament(freshTournament);
      setRefundNoticeTournament(freshTournament);
    } catch {
      // Without a fresh read the notice is left out rather than shown from a stale count.
    } finally {
      setIsCheckingPayments(false);
    }
  };

  const handleDeleteTournament = async () => {
    if (!id) return;

    try {
      const result = await cancelTournament(id);

      if (result.success) {
        toast({
          title: 'Torneo cancelado',
          description: 'El torneo ha sido cancelado exitosamente.',
        });
        // Reload tournament data
        const updatedTournament = await getTournamentById(id);
        setTournament(updatedTournament);
        // Refresh the tournament list so it's reflected in My Tournaments
        refreshTournaments();
      } else {
        toast({
          variant: 'destructive',
          title: 'Error',
          description: result.message,
        });
      }
    } catch (error) {
      handleGlobalError(
        error,
        'Error',
        'Ocurrió un error al cancelar el torneo.',
      );
    }
  };

  const toggleEditMode = () => {
    setIsEditMode(!isEditMode);
  };

  if (loading) {
    return (
      <div className="flex h-screen items-center justify-center bg-gray-100 text-gray-800">
        <div className="flex flex-col items-center space-y-5 rounded-xl bg-gray-50 p-10 shadow-md">
          <Loader2 className="h-14 w-14 animate-spin text-blue-600" />
          <h2 className="text-center text-xl font-semibold">
            Cargando torneo...
          </h2>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex h-screen items-center justify-center bg-gray-100 text-gray-800">
        <div className="flex flex-col items-center space-y-5 rounded-xl border border-red-200 bg-red-50 p-10 shadow-md">
          <div className="flex h-14 w-14 items-center justify-center rounded-full bg-red-100">
            <svg
              className="h-8 w-8 text-red-600"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z"
              />
            </svg>
          </div>
          <h2 className="text-center text-xl font-semibold text-red-800">
            Error al cargar el torneo
          </h2>
          <p className="max-w-sm text-center text-base text-red-600">{error}</p>
        </div>
      </div>
    );
  }

  if (!tournament) {
    return null;
  }

  // Note: If there's an error loading the fee, the page will still display
  // but price-related components will show "Error" labels

  return (
    <div className="container mx-auto px-2 py-6 sm:px-4">
      {/* Tournament Header */}
      <TournamentDetailsHeader
        tournament={tournament}
        onDeleteClick={openCancelDialog}
        onEditNameClick={() => setShowEditNameModal(true)}
        isEditMode={isEditMode}
        onToggleEditMode={toggleEditMode}
      />

      {/* Tournament Paused Alert */}
      {!isTournamentCancelled(tournament) && tournament.inPause && (
        <TournamentPausedAlert />
      )}

      {/* Tournament Banner */}
      <TournamentDetailsBanner
        bannerUrl={tournament.bannerUrl}
        name={tournament.name}
        onEditBannerClick={() => setShowEditBannerModal(true)}
        isEditMode={isEditMode}
      />

      <div className="mb-8">
        <TournamentDetailsInfoCard
          eloCategories={eloCategories}
          tournament={tournament}
          clubs={tournament.clubs ?? []}
          sportUid={tournament.sport?.uid}
          onEditDescriptionClick={() => setShowEditDescriptionModal(true)}
          onEditPrizesClick={() => setShowEditPrizesModal(true)}
          onEditDatesClick={() => setShowEditDatesAndScheduleModal(true)}
          onEditSignupDeadlineClick={
            canEditSignupDeadline
              ? () => setShowEditSignupDeadlineModal(true)
              : undefined
          }
          onEditPriceClick={() => setShowEditPriceModal(true)}
          onEditTeamsAllowedClick={() => setShowEditTeamsAllowedModal(true)}
          onEditPlayersPerTeamClick={() => setShowEditPlayersPerTeamModal(true)}
          onEditCategoriesClick={() => setShowEditCategoriesModal(true)}
          onEditGenderClick={() => setShowEditGenderModal(true)}
          onEditAgeRestrictionClick={() => setShowEditAgeRestrictionModal(true)}
          hasRegistrations={hasRegistrations}
          isEditMode={isEditMode}
          hasFixture={hasFixture}
          onEditOverflowGroupClassificationClick={() =>
            setShowEditOverflowGroupClassificationModal(true)
          }
          onEditAmericanGroupFormatClick={() =>
            setShowEditAmericanGroupFormatModal(true)
          }
          onEditMinSchedulesRequiredClick={() =>
            setShowEditMinSchedulesRequiredModal(true)
          }
          onEditCountsForCategoryRankingClick={() =>
            setShowEditCountsForCategoryRankingModal(true)
          }
          shouldCollapse={collapseSummaries}
        />
      </div>

      {/* Fixture */}
      <div className="mb-8">
        <FixtureSection
          tournamentId={tournament.id}
          teams={tournament.teams}
          tournament={tournament}
          onTournamentUpdated={setTournament}
        />
      </div>

      {/* Rounds (LONG tournaments only) */}
      {tournament.format === TournamentFormat.LONG && (
        <div className="mb-8">
          <RoundsCard
            // Remount per tournament: a sibling link swaps the tournament on this same page, and the
            // selected round of the previous one must not carry over.
            key={tournament.id}
            tournamentId={tournament.id}
            club={tournament.clubs?.[0] ?? null}
            matchDuration={tournament.matchDuration}
            sportUid={tournament.sport?.uid}
            fixtureReady={tournament.hasFixture}
            onTournamentRefresh={refreshTournamentData}
          />
        </div>
      )}

      {/* Participants */}
      <div className="mb-8">
        <TournamentDetailsParticipantsCard
          teams={tournament.teams}
          sportUid={tournament.sport?.uid}
          registrations={tournament.registrations}
          tournamentPaymentType={tournament.paymentType}
          hasFixture={hasFixture}
          onTeamDeleted={refreshTournamentData}
          tournamentId={tournament.id}
          tournamentStatus={tournament.status}
          tournamentInPause={tournament.inPause}
          playersPerTeam={tournament.playersPerTeam}
          minAge={tournament.minAge}
          maxAge={tournament.maxAge}
          tournamentSchedule={tournamentDays}
          finalDayRule={finalDayRuleOf(tournament)}
          knockoutStart={knockoutStartOf(tournament)}
          onTeamRegistered={refreshTournamentData}
          shouldCollapse={collapseSummaries}
        />
      </div>

      {/* Schedule - in LONG, times are handled per round, not per tournament */}
      {tournament.format !== TournamentFormat.LONG && (
        <div>
          <TournamentDetailsScheduleCard
            schedule={tournament.schedule}
            onEditScheduleClick={() => setShowEditDatesAndScheduleModal(true)}
            canEdit={canEditSchedule}
            isEditMode={isEditMode}
            shouldCollapse={collapseSummaries}
            showKnockouts={
              tournament.format === TournamentFormat.SHORT &&
              finalDayRuleOf(tournament) !== 'knockoutEveryDay'
            }
            knockoutStart={knockoutStartOf(tournament)}
          />
        </div>
      )}

      {/* Delete Tournament Dialog */}
      {showDeleteDialog && (
        <TournamentDetailsDeleteDialog
          isOpen={showDeleteDialog}
          onClose={() => setShowDeleteDialog(false)}
          onDelete={handleDeleteTournament}
          isCheckingPayments={isCheckingPayments}
          isBelowMinimumTeams={refundNoticeTournament?.isBelowMinimumTeams}
          minTeams={
            refundNoticeTournament?.minTeamsToGenerate ??
            refundNoticeTournament?.minTeamsAllowed
          }
          hasPaymentsWithoutGateway={hasPaymentsWithoutGateway(
            refundNoticeTournament?.registrations,
          )}
          hasFixture={refundNoticeTournament?.hasFixture ?? false}
        />
      )}

      {/* Always Editable Modals */}
      {showEditNameModal && (
        <EditNameModal
          isOpen={showEditNameModal}
          onClose={() => setShowEditNameModal(false)}
          currentName={tournament.name}
          onSave={async (newName) => {
            const success = await handleUpdateTournament({ name: newName });
            if (success) setShowEditNameModal(false);
            return success ?? false;
          }}
          isLoading={isUpdating}
        />
      )}

      {showEditSignupDeadlineModal && (
        <EditSignupDeadlineModal
          isOpen={showEditSignupDeadlineModal}
          onClose={() => setShowEditSignupDeadlineModal(false)}
          currentDeadline={tournament.signupDeadlineDate}
          startDate={tournament.startDate}
          onSave={async (newDeadline) => {
            const success = await handleUpdateTournament({
              signupDeadlineDate: newDeadline,
            });
            if (success) setShowEditSignupDeadlineModal(false);
            return success ?? false;
          }}
          isLoading={isUpdating}
        />
      )}

      {showEditBannerModal && (
        <EditBannerModal
          isOpen={showEditBannerModal}
          onClose={() => setShowEditBannerModal(false)}
          currentBannerUrl={tournament.bannerUrl}
          onSave={async (newBannerUrl) => {
            const success = await handleUpdateTournament({
              bannerUrl: newBannerUrl ?? undefined,
            });
            if (success) setShowEditBannerModal(false);
            return success ?? false;
          }}
          isLoading={isUpdating}
        />
      )}

      {showEditPrizesModal && (
        <EditPrizesModal
          isOpen={showEditPrizesModal}
          onClose={() => setShowEditPrizesModal(false)}
          currentPrizes={tournament.prizes}
          onSave={async (newPrizes) => {
            const success = await handleUpdateTournament({ prizes: newPrizes });
            if (success) setShowEditPrizesModal(false);
            return success ?? false;
          }}
          isLoading={isUpdating}
        />
      )}

      {showEditDescriptionModal && (
        <EditDescriptionModal
          isOpen={showEditDescriptionModal}
          onClose={() => setShowEditDescriptionModal(false)}
          currentDescription={tournament.description}
          reglamentoSource={tournament}
          onSave={async (newDescription) => {
            const success = await handleUpdateTournament({
              description: newDescription,
            });
            if (success) setShowEditDescriptionModal(false);
            return success ?? false;
          }}
          isLoading={isUpdating}
        />
      )}

      {/* Conditionally Editable Modals */}
      {showEditGenderModal && (
        <EditGenderModal
          isOpen={showEditGenderModal}
          onClose={() => setShowEditGenderModal(false)}
          currentGender={tournament.gender}
          onSave={async (newGender) => {
            const success = await handleUpdateTournament({ gender: newGender });
            if (success) setShowEditGenderModal(false);
            return success ?? false;
          }}
          isLoading={isUpdating}
        />
      )}

      {showEditDatesAndScheduleModal && (
        <EditDatesAndScheduleModal
          isOpen={showEditDatesAndScheduleModal}
          onClose={() => setShowEditDatesAndScheduleModal(false)}
          tournament={tournament}
          restrictedToSchedule={hasRegistrations}
          onUpdate={(updatedTournament) => {
            setTournament(updatedTournament);
            refreshTournaments();
          }}
        />
      )}

      {showEditPriceModal && (
        <EditPriceModal
          isOpen={showEditPriceModal}
          onClose={() => setShowEditPriceModal(false)}
          tournament={tournament}
          currentPrice={tournament.price}
          currentPaymentType={tournament.paymentType}
          onSave={async (priceData) => {
            const success = await handleUpdateTournament(priceData);
            if (success) setShowEditPriceModal(false);
            return success ?? false;
          }}
          isLoading={isUpdating}
        />
      )}

      {showEditTeamsAllowedModal && (
        <EditTeamsAllowedModal
          isOpen={showEditTeamsAllowedModal}
          onClose={() => setShowEditTeamsAllowedModal(false)}
          currentMinTeams={tournament.minTeamsAllowed}
          currentMaxTeams={tournament.maxTeamsAllowed}
          teamsPerGroups={tournament.teamsPerGroups}
          knockoutOnly={!!tournament.isKnockoutOnly}
          onSave={async (teamsData) => {
            const success = await handleUpdateTournament(teamsData);
            if (success) setShowEditTeamsAllowedModal(false);
            return success ?? false;
          }}
          isLoading={isUpdating}
        />
      )}

      {showEditPlayersPerTeamModal && (
        <EditPlayersPerTeamModal
          isOpen={showEditPlayersPerTeamModal}
          onClose={() => setShowEditPlayersPerTeamModal(false)}
          currentPlayersPerTeam={tournament.playersPerTeam}
          sportUid={tournament.sport?.uid}
          sportName={tournament.sport?.name}
          onSave={async (playersPerTeam) => {
            const success = await handleUpdateTournament({ playersPerTeam });
            if (success) setShowEditPlayersPerTeamModal(false);
            return success ?? false;
          }}
          isLoading={isUpdating}
        />
      )}

      {showEditCategoriesModal && (
        <EditCategoriesModal
          isOpen={showEditCategoriesModal}
          onClose={() => setShowEditCategoriesModal(false)}
          currentMinCategory={tournament.minCategory}
          currentMaxCategory={tournament.maxCategory}
          currentForceSum={tournament.forceSum}
          sport={tournament.sport}
          onSave={async (categoriesData) => {
            const success = await handleUpdateTournament(categoriesData);
            if (success) setShowEditCategoriesModal(false);
            return success ?? false;
          }}
          isLoading={isUpdating}
        />
      )}

      {showEditAgeRestrictionModal && (
        <EditAgeRestrictionModal
          isOpen={showEditAgeRestrictionModal}
          onClose={() => setShowEditAgeRestrictionModal(false)}
          tournament={tournament}
          onSave={async ({ minAge, maxAge }) => {
            const success = await handleUpdateTournament({ minAge, maxAge });
            if (success) setShowEditAgeRestrictionModal(false);
            return success ?? false;
          }}
          isLoading={isUpdating}
        />
      )}

      {showEditOverflowGroupClassificationModal && (
        <EditOverflowGroupClassificationModal
          isOpen={showEditOverflowGroupClassificationModal}
          onClose={() => setShowEditOverflowGroupClassificationModal(false)}
          currentClassification={
            tournament.overflowGroupClassification ??
            OverflowGroupClassification.STANDARD
          }
          onSave={async (value) => {
            const success = await handleUpdateTournament({
              overflowGroupClassification: value,
            });
            if (success) setShowEditOverflowGroupClassificationModal(false);
            return success ?? false;
          }}
          isLoading={isUpdating}
        />
      )}

      {showEditAmericanGroupFormatModal && (
        <EditAmericanGroupFormatModal
          isOpen={showEditAmericanGroupFormatModal}
          onClose={() => setShowEditAmericanGroupFormatModal(false)}
          currentFormat={resolveAmericanGroupFormat(tournament)}
          onSave={async (value) => {
            // Padel persiste en `americanGroupFormat`; squash/tennis reusan
            // `overflowGroupClassification`. Actualizamos el campo que el
            // torneo tenga definido para no romper la lectura del backend.
            const updatedFields =
              tournament.americanGroupFormat !== undefined
                ? { americanGroupFormat: value }
                : {
                    overflowGroupClassification:
                      value === AmericanGroupFormat.WINNER_LOSER
                        ? OverflowGroupClassification.EXTRA_CLASSIFIER
                        : OverflowGroupClassification.STANDARD,
                  };
            const success = await handleUpdateTournament(updatedFields);
            if (success) setShowEditAmericanGroupFormatModal(false);
            return success ?? false;
          }}
          isLoading={isUpdating}
        />
      )}

      {showEditMinSchedulesRequiredModal && (
        <EditMinSchedulesRequiredModal
          isOpen={showEditMinSchedulesRequiredModal}
          onClose={() => setShowEditMinSchedulesRequiredModal(false)}
          currentValue={tournament.minSchedulesRequired ?? 1}
          onSave={async (value) => {
            const success = await handleUpdateTournament({
              minSchedulesRequired: value,
            });
            if (success) setShowEditMinSchedulesRequiredModal(false);
            return success ?? false;
          }}
          isLoading={isUpdating}
        />
      )}

      {showEditCountsForCategoryRankingModal && (
        <EditCountsForCategoryRankingModal
          isOpen={showEditCountsForCategoryRankingModal}
          onClose={() => setShowEditCountsForCategoryRankingModal(false)}
          currentValue={tournament.countsForCategoryRanking ?? true}
          currentTarget={
            tournament.categoryRankingTarget ??
            CATEGORY_RANKING_TARGET.PLAYER_CATEGORY
          }
          currentCategory={tournament.categoryRankingCategory ?? null}
          categories={rankingRangeCategories}
          onSave={async (value, target, category) => {
            const success = await handleUpdateTournament({
              countsForCategoryRanking: value,
              categoryRankingTarget: target,
              categoryRankingCategory: category ?? undefined,
            });
            if (success) setShowEditCountsForCategoryRankingModal(false);
            return success ?? false;
          }}
          isLoading={isUpdating}
        />
      )}
    </div>
  );
};

export default TournamentDetailsPage;
